import React from 'react';
import { DefaultButton, Icon } from 'office-ui-fabric-react/lib';
import { subYears } from 'date-fns';
import { formatShortDate, formatShortTime } from '../../../utils/dates';
import { traerNombreTeam, traerTeamsJugador } from '../../../utils/equipos';
import { captainContactConflicts } from '../../../utils/playerFields';
import { validateForm, patterns } from '../../../utils/validator';

export default (parent) => {
  const columns = [
    {
      key: 'column1',
      name: 'Nombre',
      fieldName: 'name',
      isResizable: true,
      onRender: (item) => {
        return (
          <span>
            {item.name.toUpperCase()}{' '}
            {item.capitan && <Icon iconName="PartyLeader" />}
          </span>
        );
      },
    },
    {
      key: 'column11',
      name: 'Apellido',
      fieldName: 'lastName',
      isResizable: true,
      onRender: (item) => {
        return (item.lastName || '').toUpperCase();
      },
    },
    {
      key: 'column2',
      name: 'Cédula',
      fieldName: 'identification',
      isResizable: true,
    },
    {
      key: 'column3',
      name: 'Fecha de nacimiento',
      fieldName: 'birthday',
      isResizable: true,
      onRender: (item) => {
        return formatShortDate(item.birthday);
      },
    },
    {
      key: 'column4',
      name: 'Celular',
      fieldName: 'tel',
      isResizable: true,
    },
    {
      key: 'column5',
      name: '# Camisa',
      fieldName: 'shirt',
      isResizable: true,
    },
    {
      key: 'column6',
      name: 'Estado',
      fieldName: 'estado',
      isResizable: true,
    },
    {
      key: 'column7',
      name: 'e-mail',
      fieldName: 'email',
      isResizable: true,
    },
    {
      key: 'column8',
      name: 'Updated',
      fieldName: 'updatedOn',
      isResizable: true,
      onRender: (item) => {
        return (
          item.updatedOn && (
            <>
              {formatShortDate(item.updatedOn)}
              <br />
              {formatShortTime(item.updatedOn)}
            </>
          )
        );
      },
    },
  ];

  /**
   * `comentario` column, admin/superAdmin only, before Editar; `onRender` tolerates missing fields.
   */
  if (parent.esAdminOsuperAdmin()) {
    columns.push({
      key: 'column10',
      name: 'Comentario',
      fieldName: 'comentario',
      isResizable: true,
      onRender: (item) => item.comentario || '',
    });
  }

  columns.push({
    key: 'column9',
    name: '',
    onRender: (item) => {
      return (
        <DefaultButton onClick={() => parent.onItemInvoked(item)}>
          Editar
        </DefaultButton>
      );
    },
  });

  return columns;
};

export const modelUser = {
  identification: '',
  name: '',
  lastName: '',
  birthday: subYears(new Date(), 18),
  tel: '',
  shirt: '',
  activo: true,
  // Why the player is enabled or disabled: free text, optional, admin/superAdmin only.
  comentario: '',
  email: '',
  capitan: false,
  updatedOn: '',
};

/**
 * Captain-contact messages, exported so the list's live warning reuses the exact save-time wording.
 */
export const CAPTAIN_TEL_MESSAGE =
  'Este celular es el mismo del capitán del equipo';
export const CAPTAIN_EMAIL_MESSAGE =
  'Este e-mail es el mismo del capitán del equipo';

/**
 * Validate the player form, answering `{ field: message }`; a missing context flag fails closed.
 */
export const validarUsuario = async (
  user,
  nuevo,
  shirts,
  team,
  context = {},
) => {
  const { capitan, esAdminOsuperAdmin, esSuperAdmin } = context;

  const schema = {
    identification: {
      type: 'string',
      empty: false,
      min: 5,
      messages: {
        stringEmpty: 'Ingresa una cédula',
        stringMin: 'La cédula debe tener al menos 5 dígitos',
      },
    },
    name: {
      type: 'string',
      pattern: patterns.name,
      empty: false,
      messages: { stringEmpty: 'Ingresa un nombre' },
    },
    lastName: {
      type: 'string',
      pattern: patterns.name,
      empty: false,
      messages: { stringEmpty: 'Ingresa los apellidos' },
    },
    birthday: { type: 'date' },
    tel: {
      type: 'string',
      empty: false,
      length: 10,
      messages: {
        stringEmpty: 'Ingresa un celular',
        stringLength: 'Número de celular inválido',
      },
    },
    shirt: {
      type: 'string',
    },
    email: { type: 'email' },
  };

  const currentError = validateForm(user, schema);

  // `min: 5` makes a blank cédula report BOTH `stringEmpty` and `stringMin`, and the last message wins — burying "Ingresa una cédula".
  if (
    !String(user.identification ?? '').trim() &&
    currentError.identification === schema.identification.messages.stringMin
  ) {
    currentError.identification = schema.identification.messages.stringEmpty;
  }

  /**
   * Message for a cédula already in ANOTHER team; failed lookups degrade to the generic wording.
   */
  const otherTeamMessage = async (tids) => {
    const generic = 'Esta cédula ya está registrada en otro equipo';
    if (!esAdminOsuperAdmin) return generic;

    const list = Array.isArray(tids) ? tids : [];
    const results = await Promise.allSettled(
      list.map((tid) => traerNombreTeam(tid)),
    );
    const nombres = results
      .filter((result) => result.status === 'fulfilled' && result.value)
      .map((result) => result.value);
    return nombres.length ? `${generic}: ${nombres.join(', ')}` : generic;
  };

  if (!currentError.identification && nuevo) {
    const teams = await traerTeamsJugador(user);
    const otherTids = teams.filter((tid) => tid !== team);

    if (otherTids.length > 0) {
      currentError.identification = await otherTeamMessage(otherTids);
    } else if (teams.includes(team)) {
      currentError.identification =
        'Esta cédula ya existe en el sistema, puedes modificar el registro existente con estos datos nuevos o cancelar la solicitud';
    }
  }

  if (
    !currentError.identification &&
    user.oldId &&
    user.oldId !== user.identification
  ) {
    const teams = await traerTeamsJugador(user);
    const otherTids = teams.filter((tid) => tid !== team);

    if (otherTids.length > 0) {
      currentError.identification = await otherTeamMessage(otherTids);
    } else if (teams.includes(team)) {
      currentError.identification = 'Esta cédula ya existe en el equipo';
    }
  }

  if (user.shirt !== '') {
    if (shirts.includes(user.shirt)) {
      currentError.shirt = 'El # de camiseta ya está siendo utilizado';
    }
  }

  /**
   * Captain-contact conflicts block every role but superAdmin; a missing flag fails closed toward blocking.
   */
  if (!esSuperAdmin) {
    const conflicts = captainContactConflicts({
      player: user,
      captain: capitan,
    });
    if (conflicts.tel && !currentError.tel) {
      currentError.tel = CAPTAIN_TEL_MESSAGE;
    }
    if (conflicts.email && !currentError.email) {
      currentError.email = CAPTAIN_EMAIL_MESSAGE;
    }
  }

  return currentError;
};
